import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Plus, Trash, UsersThree } from "@phosphor-icons/react";
import { toast } from "sonner";

const STRATS = [
  { v: "round_robin", label: "Round-robin" },
  { v: "load_based", label: "Load-based" },
  { v: "skill_based", label: "Skill-based" },
  { v: "manual", label: "Manual" },
];

export default function Groups() {
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: "", strategy: "round_robin", member_ids: [], skills: "" });
  const [open, setOpen] = useState(false);
  const load = () => api.get("/assignment-groups").then(r => setGroups(r.data));
  useEffect(() => { load(); api.get("/users").then(r => setUsers(r.data.filter(u => u.role !== "end_user"))); }, []);

  const toggleMember = (uid) => setForm(f => ({
    ...f, member_ids: f.member_ids.includes(uid) ? f.member_ids.filter(x => x !== uid) : [...f.member_ids, uid],
  }));

  const submit = async () => {
    try {
      await api.post("/assignment-groups", { name: form.name, strategy: form.strategy, member_ids: form.member_ids, skills: form.skills.split(",").map(s=>s.trim()).filter(Boolean) });
      toast.success("Group created");
      setOpen(false); setForm({ name: "", strategy: "round_robin", member_ids: [], skills: "" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { await api.delete(`/assignment-groups/${id}`); toast.success("Deleted"); load(); };
  const patchStrategy = async (id, strategy) => { await api.put(`/assignment-groups/${id}`, { strategy }); toast.success("Strategy updated"); load(); };

  return (
    <div className="p-6" data-testid="groups-view">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-primary mb-1">Auto-Assignment</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black">Assignment Groups</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">Group agents and pick a routing strategy. Tickets matched via /routing rules with a group target will resolve to a concrete agent using the chosen strategy.</p>
        </div>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild><Button data-testid="new-group-btn" className="rounded-sm font-mono uppercase text-xs"><Plus size={14} className="mr-2"/>New Group</Button></SheetTrigger>
          <SheetContent className="bg-background border-border w-full sm:max-w-lg">
            <SheetHeader><SheetTitle className="font-display">New Assignment Group</SheetTitle></SheetHeader>
            <div className="space-y-4 mt-6">
              <div><Label className="text-xs uppercase">Name</Label><Input data-testid="group-name" className="mt-1 rounded-sm" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} /></div>
              <div><Label className="text-xs uppercase">Strategy</Label>
                <Select value={form.strategy} onValueChange={(v)=>setForm({...form,strategy:v})}>
                  <SelectTrigger data-testid="group-strategy" className="mt-1 rounded-sm"><SelectValue/></SelectTrigger>
                  <SelectContent>{STRATS.map(s => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs uppercase">Skills (for skill-based, comma-separated)</Label><Input data-testid="group-skills" className="mt-1 rounded-sm font-mono" placeholder="vpn, network, email" value={form.skills} onChange={(e)=>setForm({...form,skills:e.target.value})} /></div>
              <div>
                <Label className="text-xs uppercase">Members</Label>
                <div className="mt-2 space-y-1 max-h-56 overflow-y-auto border border-border rounded-sm p-2">
                  {users.map(u => (
                    <label key={u.id} data-testid={`group-member-${u.email}`} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-accent/40 p-1 rounded-sm">
                      <input type="checkbox" checked={form.member_ids.includes(u.id)} onChange={()=>toggleMember(u.id)} className="accent-primary" />
                      <span>{u.name}</span>
                      <span className="ml-auto text-[10px] font-mono uppercase text-muted-foreground">{u.role}</span>
                    </label>
                  ))}
                </div>
              </div>
              <Button data-testid="group-save" onClick={submit} disabled={!form.name || form.member_ids.length===0} className="w-full rounded-sm font-mono uppercase text-xs">Create Group</Button>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {groups.map(g => {
          const members = users.filter(u => g.member_ids.includes(u.id));
          return (
            <Card key={g.id} className="p-5 border-border rounded-sm">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <UsersThree size={16} className="text-primary" />
                  <div className="font-display text-lg">{g.name}</div>
                </div>
                <button data-testid={`group-del-${g.id}`} onClick={()=>del(g.id)} className="text-muted-foreground hover:text-red-700 dark:hover:text-red-400"><Trash size={14}/></button>
              </div>
              <div className="mb-3">
                <Label className="text-[10px] font-mono uppercase text-muted-foreground">Strategy</Label>
                <Select value={g.strategy} onValueChange={(v)=>patchStrategy(g.id, v)}>
                  <SelectTrigger data-testid={`group-strategy-${g.id}`} className="mt-1 rounded-sm"><SelectValue/></SelectTrigger>
                  <SelectContent>{STRATS.map(s => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="text-[10px] font-mono uppercase text-muted-foreground mb-1">Members ({members.length})</div>
              <div className="flex flex-wrap gap-1 mb-2">
                {members.map(m => <span key={m.id} className="text-[10px] font-mono px-1.5 py-0.5 border border-border">{m.name}</span>)}
              </div>
              {g.skills?.length > 0 && (
                <>
                  <div className="text-[10px] font-mono uppercase text-muted-foreground mb-1">Skills</div>
                  <div className="flex flex-wrap gap-1">{g.skills.map(s => <span key={s} className="text-[10px] font-mono px-1.5 py-0.5 border border-primary/40 text-primary">{s}</span>)}</div>
                </>
              )}
            </Card>
          );
        })}
        {groups.length === 0 && <div className="text-muted-foreground">No groups yet.</div>}
      </div>
    </div>
  );
}
